import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import paymentGatewayService from '../services/paymentGatewayService';
import MerchantProviderConfigurationModal from './MerchantProviderConfigurationModal';
import PaymentGatewayRateTable from './PaymentGatewayRateTable';
import Pagination from './Pagination';
import { EditIcon, EyeIcon, SearchIcon } from './Icons';
import { paymentGatewayConfiguration, validatePaymentGatewayConfiguration, isEnvComplete, PAYMENT_API_FIELDS, PAYMENT_BO_FIELDS, PAYMENT_CALLBACKS } from '../utils/providerConfiguration';
import './MerchantPaymentGateways.css';

const idOf = value => String(value?._id || value || '');
const asList = value => Array.isArray(value) ? value : value ? [value] : [];
const filterFields = [
  { key: 'source', label: 'Source', plural: 'sources' }, { key: 'category', label: 'Category', plural: 'categories' },
  { key: 'country', label: 'Country', plural: 'countries' }, { key: 'currency', label: 'Currency', plural: 'currencies' }
];
const emptyFilters = () => Object.fromEntries(filterFields.map(field => [field.key, '']));
const errorMessage = error => error.response?.data?.message || error.message || 'Unable to load payment gateways.';
const newAssignment = record => ({ provider_id: record, selling_rate: 0, status: 'draft', is_active: false,
  is_client_line: false, group_links: record.group_links || [], credentials: {} });

const MerchantPaymentGateways = ({ value = [], onChange, disabled = false, readOnly = false, canViewFinancial = false, canEditFinancial = false, embedded = false }) => {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [records, setRecords] = useState([]);
  const [referenceOptions, setReferenceOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [editingProvider, setEditingProvider] = useState(null);
  const [configurationError, setConfigurationError] = useState('');
  const canManage = !readOnly && !disabled;
  const showSelected = !canManage || selectedOnly;

  useEffect(() => {
    if (!canManage) return undefined;
    let active = true;
    paymentGatewayService.getMasterProviderOptions().then(result => {
      if (!result.success) throw new Error(result.message);
      if (active) setReferenceOptions(result.data);
    }).catch(err => { if (active) setError(errorMessage(err)); });
    return () => { active = false; };
  }, [canManage, refresh]);

  useEffect(() => {
    if (showSelected) return undefined;
    let active = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await paymentGatewayService.list('master-providers', { search, ...filters, is_active: true, page, limit });
        if (!result.success) throw new Error(result.message);
        if (active) {
          const lastPage = Math.max(1, result.pagination.pages);
          if (page > lastPage) { setPage(lastPage); return; }
          setRecords(result.data);
          setPagination(result.pagination);
          setError('');
        }
      } catch (err) {
        if (active) { setRecords([]); setError(errorMessage(err)); }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [search, filters, showSelected, page, limit, refresh]);

  const assignedById = new Map(value.map(item => [idOf(item.provider_id), item]));
  const selectedRecords = value.map(item => item.provider_id).filter(item => item && typeof item === 'object');
  const recordsById = new Map([...selectedRecords, ...records].map(record => [idOf(record), record]));
  const selectedMatches = value.map(item => recordsById.get(idOf(item.provider_id))).filter(Boolean).filter(record => {
    const text = [record.code, record.provider_id?.name, record.source?.name, record.description,
      ...filterFields.flatMap(field => asList(record[field.key]).flatMap(item => [item.name, item.code, item.currency_code]))].filter(Boolean).join(' ').toLowerCase();
    return text.includes(search.trim().toLowerCase()) && filterFields.every(field => !filters[field.key] || asList(record[field.key]).some(item => idOf(item) === filters[field.key]));
  });
  const selectedPage = Math.min(page, Math.max(1, Math.ceil(selectedMatches.length / limit)));
  const visibleRecords = showSelected ? selectedMatches.slice((selectedPage - 1) * limit, selectedPage * limit) : records;
  const visiblePagination = showSelected ? { page: selectedPage, limit, total: selectedMatches.length, pages: Math.ceil(selectedMatches.length / limit) } : pagination;

  const openConfiguration = record => {
    const item = assignedById.get(idOf(record)) || newAssignment(record);
    setEditingProvider(paymentGatewayConfiguration({ ...item, provider_id: record }));
    setConfigurationError('');
  };
  const toggleAssignment = record => {
    if (assignedById.has(idOf(record))) onChange(value.filter(item => idOf(item.provider_id) !== idOf(record)));
    else {
      onChange([...value, paymentGatewayConfiguration(newAssignment(record))]);
      openConfiguration(record);
    }
  };
  const applyConfiguration = () => {
    const message = validatePaymentGatewayConfiguration(editingProvider, canViewFinancial && canEditFinancial);
    if (message) { setConfigurationError(message); return; }
    const id = idOf(editingProvider.provider_id);
    const item = { ...(assignedById.get(id) || newAssignment(editingProvider.provider_id)),
      ...(canViewFinancial && canEditFinancial ? { selling_rate_rules: editingProvider.selling_rate_rules, is_client_line: editingProvider.is_client_line } : {}),
      status: editingProvider.status, is_active: editingProvider.status === 'active',
      description: editingProvider.description, documents: editingProvider.documents,
      group_links: editingProvider.group_links, credentials: editingProvider.credentials };
    onChange(assignedById.has(id) ? value.map(previous => idOf(previous.provider_id) === id ? item : previous) : [...value, item]);
    setEditingProvider(null);
  };

  return <section className={`${embedded ? 'merchant-payment-gateways-embedded' : 'card'} merchant-payment-gateways`}>
    {!embedded && <div className="merchant-payment-gateway-heading"><h3>{readOnly ? 'Payment Gateways' : 'Payment Gateway Configuration'}</h3><span>{value.length} {readOnly ? 'configured' : 'selected'}</span></div>}
    <div className="merchant-payment-gateway-toolbar">
      <div className="search-container"><div className="search-icon-wrapper"><SearchIcon /></div>
        <input className="search-input" aria-label="Search merchant payment gateways" placeholder="Search provider, code or source…" value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} />
      </div>
      {canManage && <div className="merchant-payment-gateway-scope" aria-label="Payment gateway list">
        <button type="button" aria-pressed={!selectedOnly} onClick={() => { setSelectedOnly(false); setPage(1); }}>All Providers</button>
        <button type="button" aria-pressed={selectedOnly} onClick={() => { setSelectedOnly(true); setPage(1); }}>Selected ({value.length})</button>
      </div>}
    </div>
    <div className="merchant-payment-gateway-filters">
      {filterFields.map(field => {
        const options = new Map([...asList(referenceOptions[field.key]), ...selectedRecords.flatMap(record => asList(record[field.key]))].map(item => [idOf(item), item]));
        return <label key={field.key}>{field.label}<select className="input" aria-label={`Filter payment gateways by ${field.label.toLowerCase()}`} value={filters[field.key]}
          onChange={event => { setFilters(previous => ({ ...previous, [field.key]: event.target.value })); setPage(1); }}>
          <option value="">All {field.plural}</option>
          {[...options.values()].map(item => <option key={idOf(item)} value={idOf(item)}>{item.name} ({item.currency_code || item.code})</option>)}
        </select></label>;
      })}
      <button type="button" className="btn btn-secondary" onClick={() => { setSearch(''); setFilters(emptyFilters()); setPage(1); }}>Reset Filters</button>
    </div>
    {error && <p className="merchant-payment-gateway-error" role="alert">{error} <button type="button" className="btn btn-secondary" onClick={() => setRefresh(value => value + 1)}>Retry</button></p>}
    <div className="merchant-payment-gateway-table"><table className="table-striped" aria-label="Merchant payment gateways" aria-busy={!showSelected && loading}>
      <thead><tr>{canManage && <th className="merchant-payment-gateway-selection"><span className="gateway-sr-only">Selected</span></th>}
        <th>Provider</th><th>{canViewFinancial ? 'Coverage & Selling Rates' : 'Coverage'}</th><th>Credentials</th>
        {canViewFinancial && <th>Client Line</th>}<th>Status</th><th>Actions</th></tr></thead>
      <tbody style={{ opacity: !showSelected && loading ? 0.5 : 1 }}>
        {visibleRecords.length === 0 ? <tr><td colSpan={5 + (canManage ? 1 : 0) + (canViewFinancial ? 1 : 0)} className="merchant-payment-gateway-empty">
          {!showSelected && loading ? 'Loading payment gateways…' : 'No payment gateways found.'}
        </td></tr> : visibleRecords.map(record => {
          const item = assignedById.get(idOf(record));
          const name = record.provider_id?.name || record.code;
          return <tr key={idOf(record)}>
            {canManage && <td className="merchant-payment-gateway-selection"><input type="checkbox" aria-label={`Select ${record.code} payment gateway`} checked={!!item} onChange={() => toggleAssignment(record)} /></td>}
            <td><strong>{name}</strong><span className="merchant-payment-gateway-source">{record.code}</span><span className="merchant-payment-gateway-source">{record.source?.name || '—'}</span></td>
            <td><PaymentGatewayRateTable provider={record} rules={item?.selling_rate_rules} defaultRate={item?.selling_rate ?? 0} showRates={canViewFinancial && !!item} preview /></td>
            <td>{item ? ['production', 'staging'].map(env => {
              const complete = isEnvComplete(item.credentials?.[env], [...PAYMENT_API_FIELDS, ...PAYMENT_BO_FIELDS], PAYMENT_CALLBACKS);
              return <span key={env} className={`merchant-payment-gateway-status ${complete ? 'active' : 'inactive'}`}>{env === 'production' ? 'PROD' : 'STG'} {complete ? 'READY' : 'MISSING'}</span>;
            }) : '—'}</td>
            {canViewFinancial && <td>{item ? item.is_client_line ? 'Yes' : 'No' : '—'}</td>}
            <td>{item ? <span className={`merchant-payment-gateway-status ${item.status || 'draft'}`}>{item.status || 'draft'}</span> : '—'}</td>
            <td><button type="button" className="btn merchant-payment-gateway-edit" aria-label={`${canManage ? 'Edit' : 'View'} ${name} payment gateway`} onClick={() => openConfiguration(record)}>
              {canManage ? <EditIcon size={14} /> : <EyeIcon size={14} />}{canManage ? 'Edit' : 'View'}
            </button></td>
          </tr>;
        })}
      </tbody>
    </table></div>
    <Pagination pagination={visiblePagination} onPageChange={setPage} onLimitChange={value => { setLimit(value); setPage(1); }} loading={!showSelected && loading} itemName="payment gateways" />
    {createPortal(<MerchantProviderConfigurationModal isOpen={!!editingProvider} onClose={() => setEditingProvider(null)}
      editingProvider={editingProvider} setEditingProvider={setEditingProvider} modalMasterProvider={editingProvider?.provider_id}
      canEdit={canManage} canViewProviderFinancial={canViewFinancial} canEditProviderFinancial={canEditFinancial}
      onApply={applyConfiguration} error={configurationError} />, document.body)}
  </section>;
};

export default MerchantPaymentGateways;
